import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  fallback?: React.ReactNode;
}

/** Default landing page for front-window-only users when the current route does not list 'frontwindow'. */
export const FRONT_WINDOW_REDIRECT = '/customer-service' as const;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [],
  fallback
}) => {
  const { isAuthenticated, isLoading, user, hasAnyRole, isFrontWindowOnly } = useAuth();
  const location = useLocation();
  const fallbackElement = fallback ?? <Navigate to="/login" replace />;
  
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  
  if (!isAuthenticated) {
    const targetRoute = `${location.pathname}${location.search}${location.hash ?? ''}`;
    if (targetRoute && targetRoute !== '/login') {
      sessionStorage.setItem('intended_route', targetRoute);
    }
    return <>{fallbackElement}</>;
  }

  // Wait for user data to be hydrated before evaluating roles — without this
  // guard, hasAnyRole checks against an empty array and redirects to /unauthorized.
  if (!user) {
    return <LoadingSkeleton />;
  }

  // frontwindow users can only access routes that explicitly list 'frontwindow'
  if (isFrontWindowOnly && !requiredRoles.includes('frontwindow')) {
    return <Navigate to={FRONT_WINDOW_REDIRECT} replace />;
  }

  if (requiredRoles.length > 0) {
    if (!hasAnyRole(requiredRoles)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }
  
  return <>{children}</>;
};
